import values from 'lodash/values';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { NgbDateStruct, NgbDatepickerNavigateEvent } from '@ng-bootstrap/ng-bootstrap';
import { DeliveryCalendar } from '../../models/delivery/delivery-calendar';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';

@Component({
  standalone: false,
  selector: 'ui-delivery-datepicker',
  styleUrls: ['./ui-delivery-datepicker.component.scss'],
  templateUrl: './ui-delivery-datepicker.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: UiDeliveryDatepickerComponent
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: UiDeliveryDatepickerComponent
    }
  ],
})
export class UiDeliveryDatepickerComponent implements AfterViewInit, ControlValueAccessor {
  @Input() calendar: DeliveryCalendar;
  @Input() disabled = false;
  @Input() placeholder: string;
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() placement: string;

  @Output() onMonthNavigate = new EventEmitter<string>();
  @Output() onContextInit = new EventEmitter<any>();

  @ViewChild('dp', { static: false }) dp;

  ngbModel: NgbDateStruct;

  ngAfterViewInit(): void {
    this.onContextInit.emit(this.dp);
  }

  getNgbDate(date: Date) {
    return date ? this._toDateObject(date) : null;
  }

  public onDateChanged(ngbModel: NgbDateStruct) {
    try {
      const nativeDate = this._fromDateObjectToDate(ngbModel);

      this.onChange(moment(nativeDate).format('YYYY-MM-DD'));
      this.onTouched();
    } catch (err) {
      console.warn(JSON.stringify(err));
    }
  }

  public isDateNotAvailable(date: NgbDateStruct): boolean {
    if (!this.calendar) {
      return true;
    }

    return this.calendar.isDateInactive(this._fromDateObjectToDate(date))
      || this.isFullCapacityDay(date);
  }

  public isFullCapacityDay(date: NgbDateStruct) {
    if (!this.calendar) {
      return true;
    }

    return this.calendar.isFullCapacityDay(this._fromDateObjectToDate(date));
  }

  public isModelValid(): boolean {
    return !isEmpty(this.ngbModel) && values(this.ngbModel).every(Boolean);
  }

  public isToday(date: NgbDateStruct) {
    let now = this._toDateObject(new Date());
    return now.year === date.year &&
      now.month === date.month &&
      now.day === date.day;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.calendar || !this.isModelValid()) {
      return null;
    }

    const isModelDateInactive = this.calendar.isDateInactive(this.getModelDate());
    const date = control.value;

    return isModelDateInactive ? { isModelDateInactive: { date } } : null;
  }

  public onMonthUpdated(changes: NgbDatepickerNavigateEvent) {
    if (!changes.current
      || changes.next.month === changes.current.month
      && changes.next.year === changes.current.year) {
      return;
    }

    const { year, month } = changes.next;
    const date = moment().year(year).month(month - 1).startOf('month').format('YYYY-MM-DD');
    this.onMonthNavigate.emit(date);
  }

  private getModelDate() {
    return this._fromDateObjectToDate(this.ngbModel);
  }

  private _fromDateObjectToDate(obj: any): Date {
    return moment()
      .year(obj.year)
      .month(obj.month - 1)
      .date(obj.day)
      .toDate();
  }

  private _toDateObject(date: Date) {
    const mDate = moment(date);
    return {
      day: mDate.date(),
      month: mDate.month() + 1,
      year: mDate.year()
    };
  }

  writeValue(value: any): void {
    this.ngbModel = value ? this._toDateObject(value) : null;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  private onChange(value: any): void {
  }

  private onTouched(): void {
  }
}
